<div class="cost-correction-form" [class.readonly]="viewMode === 'readonly'">
  <!-- Loading spinner -->
  @if (isLoadingData) {
    <div class="loading-container">
      <mat-spinner></mat-spinner>
      <p>Loading enrollment data...</p>
    </div>
  }

  <!-- Error message -->
  @if (loadError) {
    <div class="error-container">
      <p class="error-message">{{ loadError }}</p>
    </div>
  }

  <!-- Form content -->
  @if (!isLoadingData && !loadError) {
    <form [formGroup]="form" (ngSubmit)="onSubmit()">

      <mat-card class="form-card">
    <!-- Student/Context Information Section -->
    <div class="context-section mat-elevation-z0">
      <h3>Student Information</h3>
      <div class="context-grid">
        <mat-form-field>
          <mat-label>School Name</mat-label>
          <input matInput formControlName="schoolName" readonly>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Household Name</mat-label>
          <input matInput formControlName="householdName" readonly>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Student Name</mat-label>
          <input matInput formControlName="studentName" readonly>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Student PID</mat-label>
          <input matInput formControlName="studentPid" readonly>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Academic Year</mat-label>
          <input matInput formControlName="academicYear" readonly>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Term</mat-label>
          <input matInput formControlName="termName" readonly>
        </mat-form-field>
      </div>
    </div>

    <!-- Cost Correction Section -->
    <div class="cost-section py-4">
      <h3>Cost Correction</h3>
      <p class="instructions">Review current costs and enter corrected values. All fields are required.</p>

      <!-- Standard Cost -->
      <div class="cost-comparison-row">
        <div class="cost-label">
          <strong>Standard Cost</strong>
          <span class="cost-description">(Academic Year Total)</span>
        </div>
        <div class="cost-values">
          <mat-form-field class="current-value">
            <mat-label>Current</mat-label>
            <input matInput type="number" [value]="form.get('originalStandardCost')?.value" readonly>
          </mat-form-field>
          <span class="arrow">→</span>
          <mat-form-field class="new-value">
            <mat-label>New Value</mat-label>
            <input matInput type="number" formControlName="newStandardCost" [readonly]="viewMode === 'readonly'">
            @if (form.get('newStandardCost')?.invalid && form.get('newStandardCost')?.touched) {
              <mat-error>Cost must be a positive number</mat-error>
            }
          </mat-form-field>
        </div>
      </div>

      <!-- Individual Cost -->
      <div class="cost-comparison-row">
        <div class="cost-label">
          <strong>Individual Cost</strong>
          <span class="cost-description">(Academic Year Total)</span>
        </div>
        <div class="cost-values">
          <mat-form-field class="current-value">
            <mat-label>Current</mat-label>
            <input matInput type="number" [value]="form.get('originalIndividualCost')?.value" readonly>
          </mat-form-field>
          <span class="arrow">→</span>
          <mat-form-field class="new-value">
            <mat-label>New Value</mat-label>
            <input matInput type="number" formControlName="newIndividualCost" [readonly]="viewMode === 'readonly'">
            @if (form.get('newIndividualCost')?.invalid && form.get('newIndividualCost')?.touched) {
              <mat-error>Cost must be a positive number</mat-error>
            }
          </mat-form-field>
        </div>
      </div>

      <!-- Fall Term Cost -->
      <div class="cost-comparison-row">
        <div class="cost-label">
          <strong>Fall Term Cost</strong>
        </div>
        <div class="cost-values">
          <mat-form-field class="current-value">
            <mat-label>Current</mat-label>
            <input matInput type="number" [value]="form.get('originalFallTermCost')?.value" readonly>
          </mat-form-field>
          <span class="arrow">→</span>
          <mat-form-field class="new-value">
            <mat-label>New Value</mat-label>
            <input matInput type="number" formControlName="newTermCostFall" [readonly]="viewMode === 'readonly'">
            @if (form.get('newTermCostFall')?.invalid && form.get('newTermCostFall')?.touched) {
              <mat-error>Cost must be a positive number</mat-error>
            }
          </mat-form-field>
        </div>
      </div>

      <!-- Spring Term Cost -->
      <div class="cost-comparison-row">
        <div class="cost-label">
          <strong>Spring Term Cost</strong>
        </div>
        <div class="cost-values">
          <mat-form-field class="current-value">
            <mat-label>Current</mat-label>
            <input matInput type="number" [value]="form.get('originalSpringTermCost')?.value" readonly>
          </mat-form-field>
          <span class="arrow">→</span>
          <mat-form-field class="new-value">
            <mat-label>New Value</mat-label>
            <input matInput type="number" formControlName="newTermCostSpring" [readonly]="viewMode === 'readonly'">
            @if (form.get('newTermCostSpring')?.invalid && form.get('newTermCostSpring')?.touched) {
              <mat-error>Cost must be a positive number</mat-error>
            }
          </mat-form-field>
        </div>
      </div>
    </div>

    <!-- Reason Section -->
    <div class="reason-section">
      <mat-form-field class="full-width">
        <mat-label>Reason for Cost Correction</mat-label>
        <textarea
          matInput
          formControlName="reasonForCorrection"
          maxlength="500"
          rows="4"
          placeholder="Explain why these cost corrections are needed..."
          [readonly]="viewMode === 'readonly'"></textarea>
        <mat-hint align="end">{{ form.get('reasonForCorrection')?.value?.length || 0 }}/500</mat-hint>
        @if (form.get('reasonForCorrection')?.invalid && form.get('reasonForCorrection')?.touched) {
          <mat-error>Reason is required (max 500 characters)</mat-error>
        }
      </mat-form-field>
    </div>
    @if (viewMode === 'edit') {

      <!-- Form-level validation errors -->
      @if (form.errors?.['noChanges'] && form.touched) {
        <div class="form-level-errors mt-2">
          <mat-error>At least one cost value must be changed to submit this form.</mat-error>
        </div>
      }

      @if (form.errors?.['individualCostNotSum'] && form.touched) {
        <div class="form-level-errors mt-4">
          <mat-error>Individual cost must equal the sum of all term costs.</mat-error>
        </div>
      }
      <!-- Submit button -->
      <mat-card-actions class="pt-4">
        <button
          mat-stroked-button
          color="primary"
          type="submit"
          [disabled]="form.invalid || isSubmitting">
          @if (!isSubmitting) {
            <span>Submit Cost Correction</span>
          }
          @if (isSubmitting) {
            <span>Submitting...</span>
          }
        </button>
      </mat-card-actions>
    }
      </mat-card>
  </form>
  }
</div>
